import { Button, Toggle, Typography } from "@cur8/maneki";
import { getAndParseLocationCookie } from "lib/nekoCookies";
import { isNekoLensSite } from "lib/nekoLens";
import { useCallback } from "react";
import { useFeaturePreference } from "render/hooks/useFeaturePreference";
import { ActionsFooter } from "render/ui/layout/ActionsFooter";
import { PageFrameContent } from "render/ui/layout/PageFrameContent";
import styles from "./styles.module.sass";

interface FeatureFlagsViewProps {
  onBack(): void;
}

export function FeatureFlagsView({ onBack }: FeatureFlagsViewProps) {
  const featurePreferences = useFeaturePreference();
  // Neko Lens only runs in some markets. The toggle still shows elsewhere so
  // operators can see the setting exists, it just can't be turned on.
  const dermaQrCodeSupported = isNekoLensSite(
    getAndParseLocationCookie()?.siteId
  );

  const onToggleRoomScanWarning = useCallback(
    (isSelected: boolean) => {
      featurePreferences.set({
        ...featurePreferences.data,
        roomScanWarningEnabled: isSelected,
      });
    },
    [featurePreferences]
  );

  const onToggleDermaQrCode = (isSelected: boolean) => {
    if (!dermaQrCodeSupported) {
      return;
    }
    featurePreferences.set({
      ...featurePreferences.data,
      dermaQrCodeEnabled: isSelected,
    });
  };

  const onToggleCheckoutFlow = useCallback(
    (isSelected: boolean) => {
      featurePreferences.set({
        ...featurePreferences.data,
        checkoutFlowEnabled: isSelected,
      });
    },
    [featurePreferences]
  );

  const onTogglePressureRetestAlerts = useCallback(
    (isSelected: boolean) => {
      featurePreferences.set({
        ...featurePreferences.data,
        pressureRetestAlertsEnabled: isSelected,
      });
    },
    [featurePreferences]
  );

  const onToggleBloodDrawStep = useCallback(
    (isSelected: boolean) => {
      featurePreferences.set({
        ...featurePreferences.data,
        bloodDrawStepEnabled: isSelected,
      });
    },
    [featurePreferences]
  );

  const onToggleBloodLabStatus = useCallback(
    (isSelected: boolean) => {
      featurePreferences.set({
        ...featurePreferences.data,
        bloodLabStatusEnabled: isSelected,
      });
    },
    [featurePreferences]
  );

  return (
    <PageFrameContent>
      <div className={styles.FeatureFlagsView}>
        <Typography variant="display-s">Settings</Typography>

        <div className={styles.featureToggle}>
          <Toggle
            aria-label="Room scan warning"
            isSelected={featurePreferences.data.roomScanWarningEnabled}
            onChange={onToggleRoomScanWarning}
          />
          <div className={styles.text}>
            <Typography variant="label-m">Room scan warning</Typography>
            <Typography variant="body-s">
              Warn before assigning a room to a member when another member is
              actively being scanned in it.
            </Typography>
          </div>
        </div>

        <div className={styles.featureToggle}>
          <Toggle
            aria-label="Derma QR code"
            isDisabled={!dermaQrCodeSupported}
            isSelected={
              dermaQrCodeSupported && featurePreferences.data.dermaQrCodeEnabled
            }
            onChange={onToggleDermaQrCode}
          />
          <div className={styles.text}>
            <Typography
              variant="label-m"
              color={dermaQrCodeSupported ? "default" : "subtle"}
            >
              Derma QR code
            </Typography>
            <Typography variant="body-s" color="subtle">
              {dermaQrCodeSupported
                ? "Show the Neko Lens QR code for Derma exams in this clinic."
                : "Neko Lens is not available in this clinic's market."}
            </Typography>
          </div>
        </div>

        <div className={styles.featureToggle}>
          <Toggle
            aria-label="Check-out flow"
            isSelected={featurePreferences.data.checkoutFlowEnabled}
            onChange={onToggleCheckoutFlow}
          />
          <div className={styles.text}>
            <Typography variant="label-m">Check-out flow</Typography>
            <Typography variant="body-s">
              Turn on the reworked check-out steps in this clinic. Check-out
              still works with this off since it keeps the current screens.
            </Typography>
          </div>
        </div>
        <div className={styles.featureToggle}>
          <Toggle
            aria-label="Blood pressure retest alerts"
            isSelected={featurePreferences.data.pressureRetestAlertsEnabled}
            onChange={onTogglePressureRetestAlerts}
          />
          <div className={styles.text}>
            <Typography variant="label-m">
              Blood pressure retest alerts
            </Typography>
            <Typography variant="body-s">
              Flag arm and ankle readings that disagree, or that look too far
              off to be real, so the operator can retest before the member
              leaves.
            </Typography>
          </div>
        </div>

        <div className={styles.featureToggle}>
          <Toggle
            aria-label="Blood draw step"
            isSelected={featurePreferences.data.bloodDrawStepEnabled}
            onChange={onToggleBloodDrawStep}
          />
          <div className={styles.text}>
            <Typography variant="label-m">Blood draw step</Typography>
            <Typography variant="body-s">
              Present tube confirmation as its own step after the skin scanner,
              and move it up to second in the scan progress list. With this off
              it stays where it is in the list.
            </Typography>
          </div>
        </div>

        <div className={styles.featureToggle}>
          <Toggle
            aria-label="Blood tube lab status"
            isSelected={featurePreferences.data.bloodLabStatusEnabled}
            onChange={onToggleBloodLabStatus}
          />
          <div className={styles.text}>
            <Typography variant="label-m">Blood tube lab status</Typography>
            <Typography variant="body-s">
              Show what the lab has done with each tube, so a rejection reaches
              the operator while the member is still in the clinic. Adds a
              per-visit tube count to the blood step.
            </Typography>
          </div>
        </div>
      </div>

      <ActionsFooter
        left={
          <Button variant="secondary" onPress={onBack} fillContainer>
            Back
          </Button>
        }
      />
    </PageFrameContent>
  );
}
